import { useCallback, useMemo } from 'react';
import { Trans } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { currency } from '@frontend/design-system';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { EnrollmentBaseStep } from '../atoms/step/enrollment-base-step';
import { EnrollmentStepPerformanceWrapper } from '../atoms/step/enrollment-step-performance-wrapper';
import { useCareCreditEnrollmentContext } from '../context/care-credit-enrollment.context';
import { CareCreditEnrollmentMultiSteps } from '../enrollment-multi-step.types';

const CURRENT_STEP: CareCreditEnrollmentMultiSteps = 'application-confirmation-result';

export const Step5ApplicationResult = () => {
  return (
    <EnrollmentStepPerformanceWrapper step={CURRENT_STEP}>
      <Step5ApplicationResultBody />
    </EnrollmentStepPerformanceWrapper>
  );
};

const Step5ApplicationResultBody = () => {
  const { t } = useCareCreditTranslations();

  const { applicationConfirmationResult, onCancel, onGoToCareCreditCollect, invoice, person } =
    useCareCreditEnrollmentContext();

  const onClickPrimary = useCallback(() => {
    onGoToCareCreditCollect(applicationConfirmationResult!);
  }, [onGoToCareCreditCollect, applicationConfirmationResult]);

  const onClickSecondary = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const label = useMemo(() => {
    if (applicationConfirmationResult?.status === 'success') {
      return t('Application approved');
    }
    if (applicationConfirmationResult?.status === 'declined') {
      return t('Application declined');
    }

    if (applicationConfirmationResult?.status === 'pending') {
      return t('Application pending');
    }

    return t('Application Status Unknown');
  }, [applicationConfirmationResult, t]);

  const content = useMemo(() => {
    if (applicationConfirmationResult?.status === 'success') {
      return (
        <Trans
          t={t}
          components={{ b: <b /> }}
          i18nKey={`{{fullName}} is approved for a CareCredit credit card with a <b>{{amount}} credit limit.</b>`}
          values={{
            fullName: getFullName({ firstName: person?.firstName, lastName: person?.lastName }),
            amount: currency(
              applicationConfirmationResult.statusPayload?.accountInfo?.plccAccountInfo?.creditLine || 0,
              {
                decimals: 0,
              }
            ),
          }}
        />
      );
    }

    if (applicationConfirmationResult?.status === 'pending') {
      return (
        <Trans
          t={t}
          components={{ b: <b /> }}
          i18nKey={`Please have applicant call 1-800-924-2927 to proceed with the application. Reference application number <b>{{applicationNumber}}</b>. Additional information will be sent via mail from Synchrony Bank within 7-10 business days requesting this information.`}
          values={{
            applicationNumber: applicationConfirmationResult.statusPayload?.accountInfo?.plccAccountInfo?.accountNumber,
          }}
        />
      );
    }

    if (applicationConfirmationResult?.status === 'declined') {
      return t(
        'CareCredit was unable to approve this application at this time.  A written explanation will be sent in the mail within 7-10 days regarding the reasons for the decision and the source of the information utilized.  It will include a number to call for questions.'
      );
    }

    return t('Failed to get application result.');
  }, [applicationConfirmationResult, t]);

  return (
    <EnrollmentBaseStep
      id={CURRENT_STEP}
      label={label}
      isValid={true}
      footer={{
        primary:
          applicationConfirmationResult?.status === 'success' && !!invoice?.id
            ? { children: t('Make a Payment') }
            : null,
        onClickPrimary: onClickPrimary,
        secondary: {
          children: t('Close'),
        },
        onClickSecondary: onClickSecondary,
        showBackButton: false,
      }}
    >
      <div>{content}</div>
    </EnrollmentBaseStep>
  );
};
